import { useState } from 'react';
import { X, Calendar, FileText, Tag } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { createReservationRequest } from '../lib/api';
import { useToast } from './ui/use-toast';
import { Machine } from '../types/map';

interface ReservationRequestModalProps {
  selectedMachines: Machine[];
  onClose: () => void;
  onSuccess: () => void;
}

export function ReservationRequestModal({ selectedMachines, onClose, onSuccess }: ReservationRequestModalProps) {
  const [reservationType, setReservationType] = useState('');
  const [customReservationType, setCustomReservationType] = useState('');
  const [notes, setNotes] = useState('');
  const [requestedStart, setRequestedStart] = useState('');
  const [requestedEnd, setRequestedEnd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedMachines.length) {
      toast({
        title: 'Error',
        description: 'No machines selected',
        variant: 'destructive',
      });
      return;
    }

    if (!reservationType) {
      toast({
        title: 'Error',
        description: 'Please select a reservation type',
        variant: 'destructive',
      });
      return;
    }

    if (reservationType === 'Custom' && !customReservationType.trim()) {
      toast({
        title: 'Error',
        description: 'Please specify your custom reservation type',
        variant: 'destructive',
      });
      return;
    }

    // Check if any selected machine already has a pending/approved reservation
    const reservedMachines = selectedMachines.filter(m => 
      m.reservationInfo?.hasPendingReservation || m.reservationInfo?.hasApprovedReservation
    );

    if (reservedMachines.length > 0) {
      toast({
        title: 'Error',
        description: `${reservedMachines.length} machine(s) already have pending or approved reservations`,
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);
      const machineIds = selectedMachines.map(m => m.id);
      
      const finalReservationType = reservationType === 'Custom' ? customReservationType : reservationType;
      
      await createReservationRequest({
        machineIds,
        reservationType: finalReservationType,
        notes: notes || undefined,
        requestedStart: requestedStart || undefined,
        requestedEnd: requestedEnd || undefined,
      });

      toast({
        title: 'Success',
        description: `Reservation request submitted for ${machineIds.length} machine(s)`,
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting reservation request:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to submit reservation request',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 md:left-60 md:inset-y-0 md:right-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Request Machine Reservation</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Selected Machines ({selectedMachines.length})</h3>
            <div className="bg-gray-50 rounded-md p-4 max-h-48 overflow-y-auto space-y-2">
              {selectedMachines.map(machine => (
                <div key={machine.id} className="flex items-start gap-2 text-sm">
                  <div className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium">{machine.name}</div>
                    <div className="text-gray-500 text-xs">
                      {machine.venueType}{machine.venueName ? ` • ${machine.venueName}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="reservationType" className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Reservation Type
              </Label>
              <Select value={reservationType} onValueChange={setReservationType}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select reservation type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Long term listing">Long term listing</SelectItem>
                  <SelectItem value="Retail media">Retail media</SelectItem>
                  <SelectItem value="DOOH advertising">DOOH advertising</SelectItem>
                  <SelectItem value="Short term listing campaign">Short term listing campaign</SelectItem>
                  <SelectItem value="Campaign full takeover unit">Campaign full takeover unit</SelectItem>
                  <SelectItem value="Custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {reservationType === 'Custom' && (
              <div>
                <Label htmlFor="customReservationType">
                  Custom Reservation Type
                </Label>
                <Input
                  id="customReservationType"
                  value={customReservationType}
                  onChange={(e) => setCustomReservationType(e.target.value)}
                  placeholder="Enter your custom reservation type..."
                  className="mt-1"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="requestedStart" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Requested Start Date (Optional)
                </Label>
                <Input
                  id="requestedStart"
                  type="date"
                  value={requestedStart}
                  onChange={(e) => setRequestedStart(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Admin will set final dates</p>
              </div>

              <div>
                <Label htmlFor="requestedEnd" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Requested End Date (Optional)
                </Label>
                <Input
                  id="requestedEnd"
                  type="date"
                  value={requestedEnd}
                  onChange={(e) => setRequestedEnd(e.target.value)}
                  min={requestedStart || undefined}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Admin will set final dates</p>
              </div>
            </div>

            <div>
              <Label htmlFor="notes" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Notes (Optional)
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional information for the admin..."
                rows={4}
                className="mt-1"
              />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> Your reservation request will be reviewed by an admin. 
              You will be notified once it's approved or denied. The final reservation dates 
              will be set by the admin.
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || selectedMachines.length === 0}
              className="flex-1"
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

